import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    AlertTitle,
    Button,
    Snackbar,
    Box,
    CircularProgress
} from '@mui/material';
import config from '../../Config';

const VerificationBanner = () => {
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [shouldShow, setShouldShow] = useState(false);

    const navigate = useNavigate();

    // Функция для проверки статуса верификации в localStorage
    const checkVerificationStatus = () => {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        if (!userData?.user_info) {
            setShouldShow(false);
            return;
        }
        if (userData.user_info.email_verified) {
            setShouldShow(false);
        } else {
            setShouldShow(true);
        }
    };

    // При монтировании и при получении события обновляем статус
    useEffect(() => {
        checkVerificationStatus();
        const handleEmailVerified = () => checkVerificationStatus();
        window.addEventListener('emailVerified', handleEmailVerified);
        return () => {
            window.removeEventListener('emailVerified', handleEmailVerified);
        };
    }, []);

    if (!shouldShow) {
        return null;
    }

    const handleResendVerification = async () => {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        setLoading(true);
        try {
            const response = await fetch(`${config.apiBaseUrl}/user/resend-verification/${userData.user_info.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'user-authorization': userData.token
                }
            });

            if (response.ok) {
                const data = await response.json();
                setSnackbar({
                    open: true,
                    message: 'Verification email sent! Please check your inbox.',
                    severity: 'success'
                });
            } else {
                const errorData = await response.json();
                setSnackbar({
                    open: true,
                    message: errorData.detail || 'Failed to send verification email',
                    severity: 'error'
                });
            }
        } catch (error) {
            console.error('Error resending verification:', error);
            setSnackbar({
                open: true,
                message: 'An error occurred while sending the verification email',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleGoToVerification = () => {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        navigate('/verify-email', {
            state: {
                fromBanner: true,
                email: userData.user_info.email
            }
        });
    };

    return (
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}>
            <Alert
                severity="warning"
                sx={{
                    width: '100%',
                    borderRadius: 0,
                    px: { xs: 2, sm: 4 },
                    py: 1
                }}
                action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleGoToVerification}
                        >
                            Verify Email
                        </Button>
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleResendVerification}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {loading ? 'Sending...' : 'Resend Email'}
                        </Button>
                    </Box>
                }
            >
                <AlertTitle>Email Not Verified</AlertTitle>
                Please verify your email address to access all features. Check your inbox or spam folder.
            </Alert>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default VerificationBanner;